import React, { useState } from 'react';
import * as XLSX from 'xlsx';

import TitlePage from '../../components/general/TitlePage/TitlePage';
import TitleSectionComponent from '../../components/intern/TitleSectionComponent/TitleSectionComponent';
import FieldWithTooltip from '../../components/Tooltip/FieldWithTooltip';
import Input from '../../components/general/Input/Input';
import Textarea from '../../components/general/Textarea/Textarea';
import Select from '../../components/general/Input/Select/Select';
import RadioGroup from '../../components/RadioGroup/RadioGroup';
import CheckBoxContainer from '../../components/general/CheckBox/CheckBoxContainer';
import ButtonComponent from '../../components/general/Button/Button';

const formConfig = {
  sections: [
    {
      id: "campaign_briefing",
      title: "1. Campaign Briefing",
      fields: [
        { id: "account", label: "Account", type: "text", desc: "Name of the account or client." },
        { id: "advertiser", label: "Advertiser", type: "text", desc: "Company promoting the campaign." },
        { id: "campaign_name", label: "Campaign Name", type: "text", desc: "Internal campaign name." },
        { id: "brand", label: "Brand", type: "text", desc: "Brand associated." },
        { id: "start_date", label: "Start Date", type: "date", desc: "Campaign start date." },
        { id: "end_date", label: "End Date", type: "date", desc: "Campaign end date." },
        { id: "channels", label: "Channels", type: "multi", options: ["Display","Mobile","Video"], desc: "Where ads run." },
        { id: "third_party", label: "Third-party Tracking", type: "text", desc: "Ex: CM, IAS, Kantar." }
      ]
    },
    {
      id: "creative",
      title: "2. Creative Development",
      fields: [
        { id: "developer", label: "Who develops?", type: "select", options: ["In-house","Agency","Flashtalking / Innovid"], desc: "Responsible team." },
        { id: "dimensions", label: "Dimensions", type: "text", desc: "Ex: 300x250." },
        { id: "formats", label: "Formats", type: "multi", options: ["Standard","Rich Media","Expandable","Rich Media Video","Dynamic VAST"], desc: "Ad formats." },
        { id: "kpis", label: "Creative KPIs", type: "text", desc: "Ex: CTR, VTR." },
        { id: "is_dco", label: "Is DCO?", type: "radio", options: ["Yes","No"], desc: "Dynamic creative optimization." }
      ]
    },
    {
      id: "dco",
      title: "3. DCO & Assets",
      condition: (data) => data.is_dco === "Yes",
      fields: [
        { 
          id: "kvs", 
          label: "How many main visuals (KVs)?", 
          type: "number", 
          desc: "Example: If your campaign has 1 main visual idea, enter 1. If you have 3 very different visuals, enter 3." 
        },
        { 
          id: "sizes", 
          label: "Which banner sizes are needed?", 
          type: "text", 
          desc: "Example: 300x250, 300x600, 728x90" 
        },
        { 
          id: "versions", 
          label: "How many versions of each?", 
          type: "number", 
          desc: "Example: variations of text, language, or offer." 
        },
        { 
          id: "notes", 
          label: "Additional details", 
          type: "textarea", 
          desc: "Include any important details like animations, interactions, or specific requirements." 
        }
      ]
    },
    {
      id: "strategy",
      title: "4. Strategy & Targeting",
      fields: [
        { id: "has_strategy", label: "Has Strategy?", type: "radio", options: ["Yes","No"], desc: "If targeting exists." },
        { id: "rules", label: "Rules", type: "multi", options: ["Geo","Contextual","Retargeting","Keyword","Weather","Day/Time"], desc: "Targeting rules." }
      ]
    },
    {
      id: "feed",
      title: "5. Feed",
      fields: [
        { id: "use_feed", label: "Use Feed?", type: "radio", options: ["Yes","No"], desc: "Dynamic data usage." },
        { id: "feed_format", label: "Feed Format", type: "select", options: ["API","CSV","XML","JSON"], desc: "Data format." },
        { id: "frequency", label: "Frequency", type: "text", desc: "Update frequency." }
      ]
    },
    {
      id: "pixel",
      title: "6. Pixel Manager",
      fields: [
        { id: "pixel", label: "Use Pixel Manager?", type: "radio", options: ["Yes","No"], desc: "Tracking tool." }
      ]
    }
  ]
};

export default function Briefing() {
  const [formData, setFormData] = useState({});

  const handleChange = (id, value) => {
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const renderField = (field) => {
    const common = {
      key: field.id,
      label: field.label,
      tooltip: field.desc
    };

    switch(field.type) {
      case "text":
      case "date":
      case "number":
        return (
          <FieldWithTooltip {...common}>
            <Input
              type={field.type}
              value={formData[field.id] || ''}
              onChange={e => handleChange(field.id, e.target.value)}
            />
          </FieldWithTooltip>
        );

      case "textarea":
        return (
          <FieldWithTooltip {...common}>
            <Textarea
              value={formData[field.id] || ''}
              onChange={e => handleChange(field.id, e.target.value)}
            />
          </FieldWithTooltip>
        );

      case "select":
        return (
          <FieldWithTooltip {...common}>
            <Select
              options={field.options.map(o => ({ label: o, value: o }))}
              onChange={e => handleChange(field.id, e.target.value)}
            />
          </FieldWithTooltip>
        );

      case "radio":
        return (
          <FieldWithTooltip {...common}>
            <RadioGroup
              options={field.options.map(o => ({ label: o, value: o }))}
              value={formData[field.id]}
              onChange={val => handleChange(field.id, val)}
            />
          </FieldWithTooltip>
        );

      case "multi":
        return (
          <FieldWithTooltip
            key={field.id}
            label={field.label}
            tooltip={field.desc}
          >
            <div className="force-inline">
              <CheckBoxContainer
                options={field.options.map(o => ({ label: o, value: o }))}
                initialValues={formData[field.id] || []}
                onChange={vals => handleChange(field.id, vals)}
              />
            </div>
          </FieldWithTooltip>
        );

      default:
        return null;
    }
  };

  const handleExport = () => {
    const data = Object.entries(formData).map(([key, value]) => ({
      Field: key,
      Value: Array.isArray(value) ? value.join(', ') : value
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Briefing");

    XLSX.writeFile(wb, "campaign_briefing.xlsx");
  };

  return (
    <main className="article-page-container">
      <TitlePage title="CAMPAIGN BRIEFING" />

      <div className="article-content">
        <form>
          {formConfig.sections.map(section => {
            if (section.condition && !section.condition(formData)) return null;

            return (
              <div key={section.id}>
                <TitleSectionComponent>{section.title}</TitleSectionComponent>

                <div className="form-grid-row">
                  {section.fields.map(field => {
                    if (field.id === "rules" && formData.has_strategy !== "Yes") return null;
                    return renderField(field);
                  })}
                </div>
              </div>
            );
          })}

          <div className="container-btn-footer">
            <ButtonComponent onClick={handleExport}>
              EXPORT XLSX
            </ButtonComponent>
          </div>
        </form>
      </div>
    </main>
  );
}